import React, { useState, useEffect } from "react";
import { PageTitle } from "../../components";
import {
  Table,
  Space,
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  message,
  Upload
} from "antd";
import { useDispatch } from "react-redux";
import type { ColumnsType } from "antd/lib/table";
import { Link } from "react-router-dom";
import { PlusOutlined, LoadingOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { CURRENT_CLUB_ID } from "../../_helpers/constants";
import { teamsApi } from "../../_helpers/apiService";

interface DataType {
  key: string;
  name: string;
  image_url?: string;
}

const formItemLayout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 14 }
};

const deleteTeam = async (team: any, fetchTeams: (page: number, limit: number) => Promise<void>, currentPage: number, pageSize: number) => {
  try {
    await teamsApi.delete(team.id);
    message.success(`"${team.name}" баг амжилттай устгалаа.`);
    fetchTeams(currentPage, pageSize);
  } catch (error: any) {
    console.error("Delete team error:", error);
    // Check if the error is from the backend saying team is in use
    const errorMessage = error.response?.data?.detail || "Баг устгахад алдаа гарлаа";
    message.error(errorMessage);
  }
};

function Teams() {
  const [form] = Form.useForm();

  const [data, setData] = useState<DataType[]>([]);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const dispatch = useDispatch();
  const userData = useSelector((state: any) => state.authentication.userData);

  useEffect(() => {
    fetchTeams(currentPage, pageSize);
  }, [currentPage, pageSize]);

  const fetchTeams = async (page: number = 1, limit: number = 20) => {
    setLoading(true);
    try {
      const skip = (page - 1) * limit;
      const response = await teamsApi.getAll({
        club_id: CURRENT_CLUB_ID,
        skip: skip,
        limit: limit,
        sort_by: 'name',
        sort_order: 'asc'
      });

      const teams = response.data.items.map((team: any) => ({
        ...team,
        key: team.id.toString(),
      }));

      setData(teams);
      setTotal(response.data.total); // Set total count from backend
    } catch (error: any) {
      console.error("Fetch teams error:", error);
      message.error("Багийн мэдээлэл татахад алдаа гарлаа");
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (pagination: any) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const handleCancel = () => {
    setVisible(false);
  };

  const handleOk = () => {
    setConfirmLoading(true);
    form
      .validateFields()
      .then(async (values) => {
        try {
          await teamsApi.create({
            name: values.name,
            club_id: CURRENT_CLUB_ID,
            // image_url: logoUrl, // Can be added later
          });

          form.resetFields();
          setConfirmLoading(false);
          setVisible(false);
          message.success("Баг амжилттай нэмлээ");
          setCurrentPage(1); // Reset to first page
          fetchTeams(1, pageSize);
        } catch (error: any) {
          console.error("Create team error:", error);
          const errorMessage = error.response?.data?.detail || "Баг нэмэхэд алдаа гарлаа";
          message.error(errorMessage);
          setConfirmLoading(false);
        }
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
        setConfirmLoading(false);
      });
  };

  const columns: ColumnsType<DataType> = [
    {
      title: "№",
      dataIndex: "id",
      key: "id",
      width: 80,
      render: (text, row, index) => <>{index + 1}</>
    },
    {
      title: "Нэр",
      dataIndex: "name",
      key: "name",
      width: 250,
      render: (value, record: any) => (
        <Space size="middle">
          <Link to={`/teams/${record.key}`} key={record.key}>
            {record?.image_url && (
              <img src={record?.image_url} alt="logo" style={{ width: 50 }} />
            )}
            {value}
          </Link>
        </Space>
      )
    },
    {
      title: "Үйлдэл",
      key: "action",
      width: 150,
      render: (_, record) => (
        <Space size="middle">
          <Link to={`/teams/${record.key}`} key={record.key}>
            Засах
          </Link>
          <Popconfirm
            title="Устгахдаа итгэлтэй байна уу?"
            onConfirm={() => deleteTeam(record, fetchTeams, currentPage, pageSize)}
            okText="Тийм"
            cancelText="Үгүй"
          >
            <Button type="link">Устгах</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageTitle
        title="Багууд"
        description="Багийн жагсаалт"
        right={
          // userData.role === "leader" ? (
          //   <> </>
          // ) : (
          <Button
            shape="round"
            onClick={() => setVisible(true)}
            icon={<PlusOutlined />}
            size="large"
          >
            Баг нэмэх
          </Button>
          // )
        }
      />
      <div className="page-content-wrapper">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="card">
                <div className="card-body">
                  <div className="table-responsive">
                    <Table
                      columns={columns}
                      dataSource={data}
                      loading={loading}
                      rowKey={(item) => item.key}
                      scroll={{ x: 'max-content' }}
                      pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: total,
                        showSizeChanger: true,
                        showTotal: (total) => `Нийт: ${total}`,
                        pageSizeOptions: ['10', '20', '50', '100'],
                      }}
                      onChange={handleTableChange}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        title="Баг нэмэх"
        open={visible}
        onOk={handleOk}
        onCancel={handleCancel}
        okText="Нэмэх"
        cancelText="Болих"
        width={720}
        confirmLoading={confirmLoading}
      >
        <Form {...formItemLayout} form={form}>
          <Form.Item
            label="Нэр"
            name="name"
            rules={[{ required: true, message: "Нэр оруулна уу!" }]}
          >
            <Input />
          </Form.Item>
          {/* Image upload can be added later with a different strategy
          <Form.Item label="Upload" valuePropName="logo">
            {logoUrl ? (
              <div
                style={{
                  width: 150,
                  border: 1,
                  borderColor: "#495057",
                  borderStyle: "solid",
                }}
              >
                <img src={logoUrl} alt="logo" style={{ width: 150 }} />
              </div>
            ) : (
              <Upload
                customRequest={(e: any) => firebaseUploadHandler(e)}
                listType="picture-card"
              >
                {uploadButton}
              </Upload>
            )}
          </Form.Item> */}
        </Form>
      </Modal>
    </>
  );
}
export default Teams;
